<script setup lang="ts">
/**
 * Help: only what really exists. On the agent workstation it lists the
 * workstation's own keys (the contact search of the shell is not there).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { session } from '@/lib/auth'
import Button from '@/ui/Button.vue'
import Dialog from '@/ui/Dialog.vue'

const p = defineProps<{ open: boolean; screen?: string; workstation?: boolean; guide?: boolean }>()
const emit = defineEmits<{ close: []; guide: []; tips: [] }>()
const { t } = useI18n()
const shortcuts = computed(() => (p.workstation
  ? [
      { keys: ['F2'], label: 'shell.help.nextKey' },
      { keys: ['F3'], label: 'shell.help.dialKey' },
      { keys: [t('shell.help.spaceKey')], label: 'shell.help.hangUpKey' },
      { keys: ['1–9'], label: 'shell.help.qualifyKey' },
      { keys: ['Esc'], label: 'shell.help.escapeKey' },
      { keys: ['Tab'], label: 'shell.help.tabKey' },
    ]
  : [
      { keys: ['⌘', 'K'], label: 'shell.help.searchKey' },
      { keys: ['Esc'], label: 'shell.help.escapeKey' },
      { keys: ['Tab'], label: 'shell.help.tabKey' },
    ]))
</script>

<template>
  <Dialog :open="open" :title="t('shell.help.title')" :description="screen ? t('shell.help.screen', { screen }) : undefined"
          size="sm" @close="emit('close')">
    <h3 class="text-[13px] font-semibold">{{ t('shell.help.shortcuts') }}</h3>
    <dl class="grid gap-1.5 text-[13px]">
      <div v-for="s in shortcuts" :key="s.label" class="flex items-center justify-between gap-3">
        <dt class="text-muted-foreground">{{ t(s.label) }}</dt>
        <dd class="ltr-value flex gap-1"><kbd v-for="k in s.keys" :key="k">{{ k }}</kbd></dd>
      </div>
    </dl>
    <p v-if="session.user" class="text-[13px]">
      {{ t('shell.help.account', { name: session.user.fullName, role: t(`roles.${session.user.role}`, session.user.role) }) }}
    </p>
    <!-- The workstation's own words: an agent's line calls clients, it does not listen in. -->
    <p class="text-[13px]">
      {{ session.user?.extension ? t(workstation ? 'shell.help.sipAgent' : 'shell.help.sip', { ext: session.user.extension })
        : t(workstation ? 'shell.help.noSipAgent' : 'shell.help.noSip') }}
    </p>
    <p class="text-[12px] text-muted-foreground">{{ t(workstation ? 'shell.help.auditAgent' : 'shell.help.audit') }}</p>
    <div class="flex">
      <Button v-if="guide" size="sm" variant="outline" icon-start="help" @click="emit('guide')">{{ t('onboarding.help.guide') }}</Button>
      <Button v-else size="sm" variant="outline" icon-start="help" @click="emit('tips')">{{ t('onboarding.help.tips') }}</Button>
    </div>
  </Dialog>
</template>
